import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { isRunFinished, RecipeRun } from '@maximizer/actions/domain';
import {
  catchError,
  concat,
  defer,
  EMPTY,
  exhaustMap,
  filter,
  merge,
  Observable,
  takeUntil,
  takeWhile,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { ACTIONS_API } from './actions-api';
import { ActionsRealtimeService } from './actions-realtime.service';

export const RUN_POLL_INTERVAL_MS = 2000;

// The API gives a run 300 s; one still `running` after this was lost with the process running it.
export const RUN_POLL_DEADLINE_MS = 6 * 60 * 1000;

export const RUN_POLL_FAILURES_TOLERATED = 3;

export type RecipeRunUnfollowedReason = 'notFound' | 'unauthorized' | 'failing' | 'late';

export class RecipeRunUnfollowedError extends Error {
  constructor(
    readonly runId: string,
    readonly reason: RecipeRunUnfollowedReason,
  ) {
    super(`Recipe run ${runId} is no longer followed: ${reason}`);
    this.name = 'RecipeRunUnfollowedError';
  }
}

@Injectable()
export class RecipeRunPollerService {
  private readonly api = inject(ACTIONS_API);
  private readonly realtime = inject(ActionsRealtimeService);

  follow(id: string, createdAt?: string): Observable<RecipeRun> {
    return defer(() => {
      let failures = 0;
      let finished = false;
      const created = createdAt ? Date.parse(createdAt) : Number.NaN;
      // The deadline counts from the run's creation, but a run already past it is still asked once.
      const left = Number.isNaN(created)
        ? RUN_POLL_DEADLINE_MS
        : Math.max(created + RUN_POLL_DEADLINE_MS - Date.now(), RUN_POLL_INTERVAL_MS);

      const unfollowed = (reason: RecipeRunUnfollowedReason): Observable<never> =>
        throwError(() => new RecipeRunUnfollowedError(id, reason));

      // The hub never announces a failed run: its push only saves the wait for the next poll.
      const polls = merge(
        timer(0, RUN_POLL_INTERVAL_MS),
        this.realtime.runCompleted$.pipe(filter(({ triggerId }) => triggerId === id)),
      ).pipe(
        takeUntil(timer(left)),
        exhaustMap(() =>
          this.api.getRun(id).pipe(
            tap(() => (failures = 0)),
            catchError((error: unknown) => {
              const status = error instanceof HttpErrorResponse ? error.status : 0;
              if (status === HttpStatusCode.NotFound) {
                return unfollowed('notFound');
              }
              if (status === HttpStatusCode.Unauthorized) {
                return unfollowed('unauthorized');
              }
              // A 503 or a lost connection was never judged: the next poll asks again.
              if (status === 0 || status === HttpStatusCode.ServiceUnavailable) {
                return EMPTY;
              }
              failures += 1;
              return failures < RUN_POLL_FAILURES_TOLERATED ? EMPTY : unfollowed('failing');
            }),
          ),
        ),
        tap((run) => (finished = isRunFinished(run.status))),
        takeWhile(() => !finished, true),
      );

      return concat(
        polls,
        defer(() => (finished ? EMPTY : unfollowed('late'))),
      );
    });
  }
}
